import type { FormEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppShell } from '../components/AppShell';
import { EmptyState } from '../components/EmptyState';
import { PermissionGate } from '../components/PermissionGate';
import {
  Badge,
  Button,
  DataTable,
  type DataTableColumn,
  Input,
  Modal,
  SectionHeader,
  useActionDialog,
} from '../components/ui';
import { useAdmins } from '../features/admins/hooks/useAdmins';
import {
  useDeleteAdmin,
  useInviteAdmin,
  useSendAdminPasswordReset,
  useUpdateAdmin,
} from '../features/admins/hooks/useAdminMutations';
import { useRoles } from '../features/roles/hooks/useRoles';
import { useAssignUserRoles } from '../features/roles/hooks/useRoleMutations';
import type { AdminUser } from '../types/admins';
import type { Role } from '../types/roles';
import styles from './TeamPage.module.scss';

type AdminsTableRow = AdminUser;

export function TeamPage() {
  const { t } = useTranslation();
  const { confirmAction } = useActionDialog();
  const { data, isLoading, isError, error } = useAdmins();
  const { data: roles } = useRoles();
  const deleteMutation = useDeleteAdmin();
  const resetMutation = useSendAdminPasswordReset();
  const [editingAdmin, setEditingAdmin] = useState<AdminUser | null>(null);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [resetNotice, setResetNotice] = useState<string | null>(null);

  const admins = data?.items ?? [];

  async function handleDeleteAdmin(admin: AdminUser) {
    const confirmed = await confirmAction({
      message: t('roles.deleteAdminConfirm', { name: admin.full_name }),
      confirmLabel: t('dialog.delete'),
      tone: 'danger',
    });
    if (confirmed) deleteMutation.mutate(admin.id);
  }

  async function handlePasswordReset(admin: AdminUser) {
    const confirmed = await confirmAction({
      title: t('roles.resetPasswordTitle'),
      message: t('roles.resetPasswordConfirm', { name: admin.full_name, email: admin.email }),
      confirmLabel: t('roles.resetPasswordBtn'),
    });
    if (!confirmed) return;

    setResetNotice(null);
    resetMutation.reset();
    try {
      await resetMutation.mutateAsync(admin.id);
      setResetNotice(t('roles.resetPasswordSent', { email: admin.email }));
    } catch {
      // Mutation error is rendered below the toolbar.
    }
  }

  const columns: readonly DataTableColumn<AdminsTableRow>[] = [
    {
      key: 'name',
      header: t('roles.colName'),
      render: (admin) => admin.full_name,
    },
    {
      key: 'email',
      header: t('roles.colEmail'),
      render: (admin) => <span className={styles.secondaryText}>{admin.email}</span>,
    },
    {
      key: 'roles',
      header: t('roles.colRoles'),
      render: (admin) =>
        admin.roles.length === 0 ? (
          <span className={styles.secondaryText}>{t('roles.noRolesAssigned')}</span>
        ) : (
          <div className={styles.userRoles}>
            {admin.roles.map((r) => (
              <Badge key={r.id} variant={r.is_system ? 'accent' : 'neutral'} size="sm">
                {r.name}
              </Badge>
            ))}
          </div>
        ),
    },
    {
      key: 'actions',
      header: t('roles.colActions'),
      align: 'right',
      render: (admin) => (
        <PermissionGate permission="users:manage">
          <div className={styles.roleActions}>
            <Button
              variant="ghost"
              size="sm"
              disabled={resetMutation.isPending}
              onClick={() => void handlePasswordReset(admin)}
            >
              {resetMutation.isPending && resetMutation.variables === admin.id
                ? t('roles.resetPasswordSending')
                : t('roles.resetPasswordBtn')}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setEditingAdmin(admin)}>
              {t('roles.editBtn')}
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => void handleDeleteAdmin(admin)}
            >
              {t('roles.deleteBtn')}
            </Button>
          </div>
        </PermissionGate>
      ),
    },
  ];

  return (
    <AppShell>
      <div className={styles.page}>
        <SectionHeader title={t('team.title')} subtitle={t('team.subtitle')} />

        {isError ? (
          <EmptyState
            title={t('roles.unableToLoadUsers')}
            description={error instanceof Error ? error.message : 'Try again in a moment.'}
          />
        ) : (
          <>
            <PermissionGate permission="users:manage">
              <div className={styles.toolbar}>
                <Button variant="primary" onClick={() => setIsInviteOpen(true)}>
                  {t('roles.inviteAdminBtn')}
                </Button>
              </div>
            </PermissionGate>

            {resetNotice ? (
              <p className={styles.statusSuccess} role="status">
                {resetNotice}
              </p>
            ) : null}
            {resetMutation.isError ? (
              <p className={styles.formError} role="alert">
                {resetMutation.error instanceof Error
                  ? resetMutation.error.message
                  : t('roles.resetPasswordError')}
              </p>
            ) : null}

            {!isLoading && admins.length === 0 ? (
              <EmptyState
                title={t('roles.noUsersFound')}
                description={t('roles.noUsersDescription')}
              />
            ) : (
              <DataTable<AdminsTableRow>
                columns={columns}
                rows={admins}
                rowKey={(row) => row.id}
                isLoading={isLoading}
                emptyMessage={t('roles.noUsersFound')}
              />
            )}
          </>
        )}

        {isInviteOpen && roles ? (
          <AdminFormModal mode="invite" allRoles={roles} onClose={() => setIsInviteOpen(false)} />
        ) : null}

        {editingAdmin && roles ? (
          <AdminFormModal
            mode="edit"
            admin={editingAdmin}
            allRoles={roles}
            onClose={() => setEditingAdmin(null)}
          />
        ) : null}
      </div>
    </AppShell>
  );
}

type AdminFormModalProps = {
  readonly mode: 'invite' | 'edit';
  readonly admin?: AdminUser;
  readonly allRoles: readonly Role[];
  readonly onClose: () => void;
};

function AdminFormModal({ mode, admin, allRoles, onClose }: AdminFormModalProps) {
  const { t } = useTranslation();
  const inviteMutation = useInviteAdmin();
  const updateMutation = useUpdateAdmin();
  const assignMutation = useAssignUserRoles();

  const [email, setEmail] = useState(admin?.email ?? '');
  const [fullName, setFullName] = useState(admin?.full_name ?? '');
  const [firstName, setFirstName] = useState(admin?.first_name ?? '');
  const [lastName, setLastName] = useState(admin?.last_name ?? '');
  const [selectedRoleIds, setSelectedRoleIds] = useState<Set<string>>(
    new Set(admin?.roles.map((r) => r.id) ?? []),
  );
  const [inviteSent, setInviteSent] = useState(false);

  const isPending =
    inviteMutation.isPending || updateMutation.isPending || assignMutation.isPending;

  const errorMessage =
    inviteMutation.error instanceof Error
      ? inviteMutation.error.message
      : updateMutation.error instanceof Error
        ? updateMutation.error.message
        : assignMutation.error instanceof Error
          ? assignMutation.error.message
          : null;

  function toggleRole(roleId: string) {
    setSelectedRoleIds((prev) => {
      const next = new Set(prev);
      if (next.has(roleId)) {
        next.delete(roleId);
      } else {
        next.add(roleId);
      }
      return next;
    });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (mode === 'invite') {
      await inviteMutation.mutateAsync({
        email: email.trim(),
        full_name: fullName.trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        role_ids: Array.from(selectedRoleIds),
      });
      setInviteSent(true);
      return;
    }

    if (!admin) {
      return;
    }

    const tasks: Promise<unknown>[] = [];

    const updatePayload: Record<string, string> = {};
    if (fullName.trim() !== admin.full_name) {
      updatePayload.full_name = fullName.trim();
    }
    if (firstName.trim() !== admin.first_name) {
      updatePayload.first_name = firstName.trim();
    }
    if (lastName.trim() !== admin.last_name) {
      updatePayload.last_name = lastName.trim();
    }
    if (Object.keys(updatePayload).length > 0) {
      tasks.push(updateMutation.mutateAsync({ userId: admin.id, payload: updatePayload }));
    }

    const currentRoleIds = new Set(admin.roles.map((r) => r.id));
    const rolesChanged =
      currentRoleIds.size !== selectedRoleIds.size ||
      Array.from(selectedRoleIds).some((id) => !currentRoleIds.has(id));
    if (rolesChanged) {
      tasks.push(
        assignMutation.mutateAsync({
          userId: admin.id,
          roleIds: Array.from(selectedRoleIds),
        }),
      );
    }

    if (tasks.length > 0) {
      await Promise.all(tasks);
    }
    onClose();
  }

  const formId = 'admin-form';
  const title = mode === 'invite' ? t('roles.inviteAdminTitle') : t('roles.editAdminTitle');
  const description =
    mode === 'invite' ? t('roles.inviteAdminDescription') : t('roles.editAdminDescription');

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      subtitle={description}
      size="md"
      footer={
        inviteSent ? (
          <Button type="button" variant="primary" onClick={onClose}>
            {t('roles.close')}
          </Button>
        ) : (
          <>
            <Button type="button" variant="secondary" onClick={onClose} disabled={isPending}>
              {t('roles.cancel')}
            </Button>
            <Button type="submit" form={formId} variant="primary" disabled={isPending}>
              {isPending
                ? t('roles.saving')
                : mode === 'invite'
                  ? t('roles.invite')
                  : t('roles.save')}
            </Button>
          </>
        )
      }
    >
      {inviteSent ? (
        <p className={styles.secondaryText}>{t('roles.inviteSentMessage')}</p>
      ) : (
        <form id={formId} className={styles.form} onSubmit={handleSubmit}>
          {mode === 'invite' ? (
            <Input
              id="admin-email"
              type="email"
              label={t('roles.labelEmail')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          ) : null}
          <Input
            id="admin-full-name"
            label={t('roles.labelFullName')}
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
          />
          <Input
            id="admin-first-name"
            label={t('roles.labelFirstName')}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
          />
          <Input
            id="admin-last-name"
            label={t('roles.labelLastName')}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
          />
          <div>
            <div style={{ marginBottom: 'calc(var(--sp, 4px) * 2)' }}>
              <span className={styles.secondaryText}>{t('roles.colRoles')}</span>
            </div>
            <div className={styles.selectMulti}>
              {allRoles.map((role) => (
                <label key={role.id} className={styles.selectOption}>
                  <input
                    type="checkbox"
                    checked={selectedRoleIds.has(role.id)}
                    onChange={() => toggleRole(role.id)}
                  />
                  <span>{role.name}</span>
                  {role.is_system ? (
                    <Badge variant="accent" size="sm">
                      {t('roles.systemType')}
                    </Badge>
                  ) : null}
                </label>
              ))}
            </div>
          </div>
          {errorMessage ? <p className={styles.formError}>{errorMessage}</p> : null}
        </form>
      )}
    </Modal>
  );
}
